import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { productsAPI, inventoryAPI, myInventoryAPI, isOnline } from '../services/api';

/**
 * Product Store
 * مخزن Zustand لإدارة المنتجات والمخزون
 *
 * Protocols Applied:
 * - P-STATE-001: Zustand State Management
 * - P034: Data Validation
 * - P-DATA-001: Clear Data Structure
 * - P010: Error Handling
 * - P025: Cache Management (persist middleware + API fallback)
 * - P-API-001: RESTful API Integration
 * - P-OFFLINE-001: Offline-First Strategy
 */

const useProductStore = create(
  persist(
    (set, get) => ({
      // ==================== STATE ====================

      /**
       * Products List
       */
      products: [
        {
          id: 1,
          code: 'PROD-001',
          name: 'كولا 2 لتر',
          category: 'مشروبات',
          price: 25,
          cost: 18,
          stock: 120,
          minStock: 20,
          unit: 'علبة',
          barcode: '123456789012',
          image: null,
          lastSaleDate: '2025-12-09',
          createdAt: new Date().toISOString()
        },
        {
          id: 2,
          code: 'PROD-002',
          name: 'شيبسي كبير',
          category: 'سناكس',
          price: 10,
          cost: 7,
          stock: 8,
          minStock: 15,
          unit: 'كيس',
          barcode: '123456789013',
          image: null,
          lastSaleDate: '2025-09-15', // بطيء الحركة 🐌
          createdAt: new Date().toISOString()
        },
        {
          id: 3,
          code: 'PROD-003',
          name: 'بسكويت أوريو',
          category: 'بسكويت',
          price: 15,
          cost: 11,
          stock: 45,
          minStock: 10,
          unit: 'علبة',
          barcode: '123456789014',
          image: null,
          lastSaleDate: '2025-12-08',
          createdAt: new Date().toISOString()
        }
      ],

      /**
       * Loading State
       */
      loading: false,

      /**
       * Error State
       */
      error: null,

      /**
       * Vehicle Inventory (NEW)
       * مخزون السيارة - منفصل عن المخزن الرئيسي
       */
      vehicleInventory: [],
      vehicleStatistics: null,

      // ==================== ACTIONS ====================

      /**
       * Fetch Products from API
       * جلب المنتجات من الـ Backend
       */
      fetchProducts: async () => {
        try {
          set({ loading: true, error: null });

          // Check if online
          if (!isOnline()) {
            throw new Error('لا يوجد اتصال بالإنترنت - استخدام البيانات المحلية');
          }

          const response = await productsAPI.getAll();
          const products = response.data.data || response.data;

          set({ products, loading: false });

          return products;
        } catch (error) {
          set({ loading: false, error: error.message });
          // Keep local data if API fails
          console.warn('Failed to fetch products from API, using local cache:', error.message);
          return get().products;
        }
      },

      /**
       * Add Product
       */
      addProduct: async (product) => {
        try {
          set({ error: null, loading: true });

          // Validation (P034)
          if (!product.name || !product.price) {
            throw new Error('اسم المنتج والسعر مطلوبان');
          }

          if (product.price <= 0 || product.cost < 0) {
            throw new Error('السعر والتكلفة يجب أن تكون أرقام موجبة');
          }

          let newProduct;

          // Try API first if online
          if (isOnline()) {
            try {
              const response = await productsAPI.create(product);
              newProduct = response.data.data || response.data;
            } catch (apiError) {
              console.warn('API create failed, falling back to local:', apiError.message);
              // Fallback to local creation
              newProduct = {
                id: Date.now(),
                code: `PROD-${String(Date.now()).slice(-6)}`,
                stock: product.stock || 0,
                minStock: product.minStock || 10,
                createdAt: new Date().toISOString(),
                ...product
              };
            }
          } else {
            // Offline: Create locally
            newProduct = {
              id: Date.now(),
              code: `PROD-${String(Date.now()).slice(-6)}`,
              stock: product.stock || 0,
              minStock: product.minStock || 10,
              createdAt: new Date().toISOString(),
              ...product
            };
          }

          set((state) => ({
            products: [...state.products, newProduct],
            loading: false
          }));

          return newProduct;
        } catch (error) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      /**
       * Update Product
       */
      updateProduct: async (id, updates) => {
        try {
          set({ error: null, loading: true });

          // Try API first if online
          if (isOnline()) {
            try {
              await productsAPI.update(id, updates);
            } catch (apiError) {
              console.warn('API update failed, updating locally:', apiError.message);
            }
          }

          // Update locally
          set((state) => ({
            products: state.products.map((p) =>
              p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
            ),
            loading: false
          }));
        } catch (error) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      /**
       * Delete Product
       */
      deleteProduct: async (id) => {
        try {
          set({ error: null, loading: true });

          // Try API first if online
          if (isOnline()) {
            try {
              await productsAPI.delete(id);
            } catch (apiError) {
              console.warn('API delete failed, deleting locally:', apiError.message);
            }
          }

          // Delete locally
          set((state) => ({
            products: state.products.filter((p) => p.id !== id),
            loading: false
          }));
        } catch (error) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      /**
       * Get Product by ID
       */
      getProductById: (id) => {
        return get().products.find((p) => p.id === id);
      },

      /**
       * Get Product by Code
       */
      getProductByCode: (code) => {
        return get().products.find((p) => p.code === code);
      },

      /**
       * Get Product by Barcode
       */
      getProductByBarcode: (barcode) => {
        return get().products.find((p) => p.barcode === barcode);
      },

      /**
       * Search Products
       */
      searchProducts: (query) => {
        const lowerQuery = query.toLowerCase();
        return get().products.filter(
          (p) =>
            p.name.toLowerCase().includes(lowerQuery) ||
            p.code.toLowerCase().includes(lowerQuery) ||
            (p.barcode && p.barcode.includes(query))
        );
      },

      /**
       * Get Products by Category
       */
      getProductsByCategory: (category) => {
        return get().products.filter((p) => p.category === category);
      },

      /**
       * Get Low Stock Products
       */
      getLowStockProducts: () => {
        return get().products.filter((p) => p.stock < p.minStock);
      },

      /**
       * Get Out of Stock Products
       */
      getOutOfStockProducts: () => {
        return get().products.filter((p) => p.stock === 0);
      },

      /**
       * Get Slow Moving Products
       * Products with no sales in the last 60 days
       */
      getSlowMovingProducts: (days = 60) => {
        const today = new Date();
        return get().products.filter((p) => {
          if (!p.lastSaleDate) return true; // No sales yet

          const lastSale = new Date(p.lastSaleDate);
          const daysSinceLastSale = Math.floor((today - lastSale) / (1000 * 60 * 60 * 24));

          return daysSinceLastSale > days;
        }).map((p) => {
          const lastSale = p.lastSaleDate ? new Date(p.lastSaleDate) : null;
          const daysSinceLastSale = lastSale
            ? Math.floor((today - lastSale) / (1000 * 60 * 60 * 24))
            : null;

          return {
            ...p,
            daysSinceLastSale
          };
        });
      },

      /**
       * Adjust Stock (Add or Reduce)
       */
      adjustStock: (id, quantity, type = 'add') => {
        try {
          set({ error: null });

          const product = get().getProductById(id);
          if (!product) {
            throw new Error('المنتج غير موجود');
          }

          const newStock = type === 'add' ? product.stock + quantity : product.stock - quantity;

          if (newStock < 0) {
            throw new Error('الكمية المطلوبة غير متوفرة في المخزون');
          }

          get().updateProduct(id, { stock: newStock });

          return newStock;
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      /**
       * Add Stock
       */
      addStock: (id, quantity) => {
        return get().adjustStock(id, quantity, 'add');
      },

      /**
       * Reduce Stock
       */
      reduceStock: (id, quantity) => {
        return get().adjustStock(id, quantity, 'reduce');
      },

      /**
       * Get Total Products Count
       */
      getTotalProducts: () => {
        return get().products.length;
      },

      /**
       * Get Total Stock Value
       */
      getTotalStockValue: () => {
        return get().products.reduce((total, p) => total + p.stock * p.cost, 0);
      },

      /**
       * Get Categories
       */
      getCategories: () => {
        const categories = new Set(get().products.map((p) => p.category));
        return Array.from(categories);
      },

      /**
       * Get inventory statistics (إحصائيات المخزون)
       * @returns {Promise<Object>} Inventory statistics
       */
      fetchInventoryStatistics: async () => {
        set({ loading: true, error: null });
        try {
          const response = await inventoryAPI.getStatistics();
          return response.data.data;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل إحصائيات المخزون';
          set({ error: errorMessage, loading: false });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get products by stock status
       * @param {string} status - normal, low, out, slow
       * @returns {Promise<Array>} Products with specified status
       */
      fetchProductsByStatus: async (status) => {
        set({ loading: true, error: null });
        try {
          const response = await inventoryAPI.getByStatus(status);
          return response.data.data.products;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل المنتجات';
          set({ error: errorMessage, loading: false });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get low stock products from API
       * @returns {Promise<Array>} Low stock products
       */
      fetchLowStockProducts: async () => {
        set({ loading: true, error: null });
        try {
          const response = await inventoryAPI.getLowStock();
          return response.data.data.products;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل الأصناف القريبة من النفاد';
          set({ error: errorMessage, loading: false });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get out of stock products from API
       * @returns {Promise<Array>} Out of stock products
       */
      fetchOutOfStockProducts: async () => {
        set({ loading: true, error: null });
        try {
          const response = await inventoryAPI.getOutOfStock();
          return response.data.data.products;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل الأصناف النافدة';
          set({ error: errorMessage, loading: false });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get slow moving products
       * @returns {Promise<Array>} Slow moving products
       */
      fetchSlowMovingProducts: async () => {
        set({ loading: true, error: null });
        try {
          const response = await inventoryAPI.getSlowMoving();
          return response.data.data.products;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل الأصناف الراكدة';
          set({ error: errorMessage, loading: false });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get fast moving products (top sellers)
       * @param {number} limit - Number of products to fetch
       * @returns {Promise<Array>} Fast moving products
       */
      fetchFastMovingProducts: async (limit = 10) => {
        set({ loading: true, error: null });
        try {
          const response = await inventoryAPI.getFastMoving(limit);
          return response.data.data.products;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل الأصناف الأكثر مبيعاً';
          set({ error: errorMessage, loading: false });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get total inventory value
       * @returns {Promise<Object>} Inventory value breakdown
       */
      fetchInventoryValue: async () => {
        set({ loading: true, error: null });
        try {
          const response = await inventoryAPI.getValue();
          return response.data.data;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل قيمة المخزون';
          set({ error: errorMessage, loading: false });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Fetch Representative's Vehicle Inventory (NEW)
       * جلب مخزون السيارة الخاص بالمندوب
       * @returns {Promise<void>}
       */
      fetchMyVehicleInventory: async () => {
        set({ loading: true, error: null });
        try {
          const response = await myInventoryAPI.getAll();
          const inventory = response.data.data.inventory || [];
          const statistics = response.data.data.statistics || null;

          set({
            vehicleInventory: inventory,
            vehicleStatistics: statistics,
            loading: false
          });

          return { inventory, statistics };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل مخزون السيارة';
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      /**
       * Get specific product from vehicle inventory
       * @param {number} productId - Product ID
       * @returns {Promise<Object>} Product details
       */
      fetchMyVehicleProduct: async (productId) => {
        set({ loading: true, error: null });
        try {
          const response = await myInventoryAPI.getProduct(productId);
          const product = response.data.data;
          set({ loading: false });
          return product;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل بيانات المنتج';
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      /**
       * Search Vehicle Inventory
       * @param {string} query - Search query
       * @returns {Array} Filtered products
       */
      searchVehicleInventory: (query) => {
        const lowerQuery = query.toLowerCase();
        return get().vehicleInventory.filter(
          (p) =>
            p.product_name.toLowerCase().includes(lowerQuery) ||
            (p.product_code && p.product_code.toLowerCase().includes(lowerQuery))
        );
      },

      /**
       * Filter vehicle inventory by stock status
       * @param {string} status - normal, low, out
       * @returns {Array} Filtered products
       */
      filterVehicleInventoryByStatus: (status) => {
        if (status === 'all') return get().vehicleInventory;
        return get().vehicleInventory.filter((p) => p.stock_status === status);
      },

      /**
       * Clear Error
       */
      clearError: () => set({ error: null }),

      /**
       * Reset Store (for testing)
       */
      resetStore: () =>
        set({
          products: [],
          vehicleInventory: [],
          vehicleStatistics: null,
          loading: false,
          error: null
        })
    }),
    {
      name: 'product-storage', // LocalStorage key
      partialize: (state) => ({ products: state.products }) // Only persist products
    }
  )
);

export default useProductStore;
